import debug from 'debug';

import { BaseActions } from '../Common/Actions/BaseActions';
import { execAction } from '../Common/Actions/handler';
import { ResponseEither } from '../Common/ResponseEither';
import { IRaffleDealAllHisotryRequestParams } from './interfaces/IRaffleDealAllHistoryRequestParams';
import { IRaffleDealHistoryPayload, IRaffleDealV3HistoryPayload } from './interfaces/IRaffleDealHistoryPayload';
import { IRaffleDealV3ParticipantsCountPayload } from './interfaces/IRaffleDealParticipantsCountPayload';
import { IRaffleDealParticipantsCountRequestParams } from './interfaces/IRaffleDealParticipantsCountRequestParams';
import { IRaffleDealV3ParticipatePayload } from './interfaces/IRaffleDealParticipatePayload';
import { IRaffleDealParticipateRequestParams } from './interfaces/IRaffleDealParticipateRequestParams';
import { IRaffleDealParticipationHistoryRequestParams } from './interfaces/IRaffleDealParticipationHistoryRequestParams';
import {
  IRaffleDealPromotionsPayload,
  IRaffleDealV3PromotionsPayload,
} from './interfaces/IRaffleDealPromotionsPayload';
import { IRaffleDealPromotionsRequestParams } from './interfaces/IRaffleDealPromotionsRequestParams';
import { IRaffleDealGroupWinHistoryPayload } from './interfaces/IRaffleDealWinHistoryPayload';
import { IRaffleDealV3WinHistoryPayload } from './interfaces/IRaffleDealWinHistoryPayload';
import { IRaffleDealWinHistoryRequestParams } from './interfaces/IRaffleDealWinHistoryRequestParams';
import { IRaffleDealV3WinnersPayload, IRaffleDealWinnersPayload } from './interfaces/IRaffleDealWinnersPayload';
import { IRaffleDealWinnersRequestParams } from './interfaces/IRaffleDealWinnersRequestParams';
import { RaffleDealRequestBuilder } from './RaffleDealRequestBuilder';

const log = debug('sunny:RaffleDealActions');

export class RaffleDealActions extends BaseActions<RaffleDealRequestBuilder> {
  promotions(params: IRaffleDealPromotionsRequestParams): Promise<ResponseEither<IRaffleDealPromotionsPayload>> {
    log('Raffle Deal Promotions');
    return execAction('get promotions', this.requestBuilder.getPromotions(params));
  }

  allHistory(params: IRaffleDealAllHisotryRequestParams): Promise<ResponseEither<IRaffleDealHistoryPayload>> {
    log('Raffle Deal All History');
    return execAction('get all history', this.requestBuilder.getAllHistory(params));
  }

  participationHistory(
    params: IRaffleDealParticipationHistoryRequestParams,
  ): Promise<ResponseEither<IRaffleDealHistoryPayload>> {
    log('Raffle Deal Participation History');
    return execAction('get participation history', this.requestBuilder.getParticipationHistory(params));
  }

  groupWinHistory(
    params: IRaffleDealWinHistoryRequestParams,
  ): Promise<ResponseEither<IRaffleDealGroupWinHistoryPayload>> {
    log('Raffle Deal Group Win History');
    return execAction('get group win history', this.requestBuilder.getGroupWinHistory(params));
  }

  winners(params: IRaffleDealWinnersRequestParams): Promise<ResponseEither<IRaffleDealWinnersPayload>> {
    log('Raffle Deal Winners V2');
    return execAction('get winners v2', this.requestBuilder.getWinners(params));
  }

  // --------------------------------------------------------------------
  // ------------------------------ V3 API ------------------------------
  // --------------------------------------------------------------------

  /**
   * 래플딜 프로모션 API v3 - 1. 프로모션 정보 조회
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869237 API 문서}
   */
  getRafflePromotions(
    params: IRaffleDealPromotionsRequestParams,
  ): Promise<ResponseEither<IRaffleDealV3PromotionsPayload>> {
    log('Raffle Deal V3 Promotions');
    return execAction('get v3 promotions', this.requestBuilder.createGetRafflePromotions(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 2. 응모하기
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869243 API 문서}
   */
  postParticipate(
    params: IRaffleDealParticipateRequestParams,
  ): Promise<ResponseEither<IRaffleDealV3ParticipatePayload>> {
    log('Raffle Deal V3 Participate');
    return execAction('post v3 participate', this.requestBuilder.createPostParticipate(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 3. 전체내역 조회하기
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869247 API 문서}
   */
  getAllHistory(params: IRaffleDealAllHisotryRequestParams): Promise<ResponseEither<IRaffleDealV3HistoryPayload>> {
    log('Raffle Deal V3 All History');
    return execAction('get v3 all history', this.requestBuilder.createGetAllHistory(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 4. 사용자별 응모내역 조회하기
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869253 API 문서}
   */
  getParticipationHistory(
    params: IRaffleDealParticipationHistoryRequestParams,
  ): Promise<ResponseEither<IRaffleDealV3HistoryPayload>> {
    log('Raffle Deal V3 Participation History');
    return execAction('get v3 participation history', this.requestBuilder.createGetParticipationHistory(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 5. 사용자별 당첨내역 조회
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869257 API 문서}
   */
  getWinHistory(params: IRaffleDealWinHistoryRequestParams): Promise<ResponseEither<IRaffleDealV3WinHistoryPayload>> {
    log('Raffle Deal V3 Win History');
    return execAction('get v3 win history', this.requestBuilder.createGetWinHistory(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 6. Deal별 당첨자 목록 조회
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869261 API 문서}
   */
  getWinners(params: IRaffleDealWinnersRequestParams): Promise<ResponseEither<IRaffleDealV3WinnersPayload>> {
    log('Raffle Deal V3 Winners');
    return execAction('get v3 winners', this.requestBuilder.createGetWinners(params));
  }

  /**
   * 래플딜 프로모션 API v3 - 7. 응모자수 확인
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70869265 API 문서}
   */
  getParticipantsCount(
    params: IRaffleDealParticipantsCountRequestParams,
  ): Promise<ResponseEither<IRaffleDealV3ParticipantsCountPayload>> {
    log('Raffle Deal V3 Participants Count');
    return execAction('get v3 participants count', this.requestBuilder.createGetParticipantsCount(params));
  }
}
